package com.daytimegaming.ourdailyfinances.presentation.manageaccounts

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.data.settings.SettingsManager
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidEventBus
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidItemsEventBus
import com.daytimegaming.ourdailyfinances.domain.repository.AuthRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.RealEstateUseCase
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ManageAccountsViewModel(
    private val accountUseCase: AccountUseCase,
    private val plaidItemsEventBus: PlaidItemsEventBus,
    private val settingsManager: SettingsManager,
    private val authRepository: AuthRepository,
    private val plaidEventBus: PlaidEventBus,
    private val realEstateUseCase: RealEstateUseCase,
) : ViewModel() {

    private val _state = MutableStateFlow<ManageAccountsScreenState>(ManageAccountsScreenState.Loading)
    val state = _state.asStateFlow()

    private val _plaidLinkTokenEvent = MutableSharedFlow<String>(extraBufferCapacity = 1)
    val plaidLinkTokenEvent: SharedFlow<String> = _plaidLinkTokenEvent.asSharedFlow()

    private var updatingItemId: String? = null

    fun requestUpdateLinkToken(itemId: String) {
        updatingItemId = itemId
        viewModelScope.launch {
            try {
                val token = accountUseCase.CreateUpdateLinkToken(itemId)
                _plaidLinkTokenEvent.emit(token)
            } catch (e: Exception) {
                updatingItemId = null
                _state.update { ManageAccountsScreenState.Error(e.message ?: "Failed to generate update link token") }
            }
        }
    }

    fun requestConsentUpdateLinkToken(itemId: String) {
        updatingItemId = itemId
        viewModelScope.launch {
            try {
                val token = accountUseCase.CreateConsentUpdateLinkToken(itemId)
                _plaidLinkTokenEvent.emit(token)
            } catch (e: Exception) {
                updatingItemId = null
                _state.update { ManageAccountsScreenState.Error(e.message ?: "Failed to generate consent update link token") }
            }
        }
    }

    private var currentUserId: String? = null
    private var loadJob: kotlinx.coroutines.Job? = null

    init {
        viewModelScope.launch {
            authRepository.currentUser().collect { user ->
                currentUserId = user?.uid
                load()
            }
        }
        viewModelScope.launch {
            plaidEventBus.events.collect { result ->
                if (updatingItemId != null) {
                    updatingItemId = null
                    _state.update { ManageAccountsScreenState.Loading }
                    try {
                        accountUseCase.GetAccounts().first { it is Response.Success || it is Response.Error }
                    } catch (e: Exception) {
                        // Ignore and proceed to load
                    }
                    plaidItemsEventBus.itemsChanged()
                    load()
                }
            }
        }
    }

    fun load() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch {
            _state.update { ManageAccountsScreenState.Loading }
            combine(
                accountUseCase.GetPlaidItems(),
                accountUseCase.GetAccounts(),
                realEstateUseCase.GetRealEstateHoldings()
            ) { itemsResponse, accountsResponse, realEstateResponse ->
                when {
                    itemsResponse is Response.Error -> ManageAccountsScreenState.Error(itemsResponse.message)
                    accountsResponse is Response.Error -> ManageAccountsScreenState.Error(accountsResponse.message)
                    realEstateResponse is Response.Error -> ManageAccountsScreenState.Error(realEstateResponse.message)
                    itemsResponse is Response.Loading || accountsResponse is Response.Loading || realEstateResponse is Response.Loading -> ManageAccountsScreenState.Loading
                    itemsResponse is Response.Success && accountsResponse is Response.Success && realEstateResponse is Response.Success -> {
                        val items = itemsResponse.data
                        val mappedAccounts = accountsResponse.data.map { account ->
                            val institutionName = items.find { it.itemId == account.itemId }?.institutionName
                            account.copy(institutionName = institutionName)
                        }
                        
                        val userId = currentUserId
                        val savedOrder = if (userId != null) settingsManager.getAccountOrder(userId) else emptyList()
                        
                        val orderedAccounts = if (savedOrder.isNotEmpty()) {
                            mappedAccounts.sortedWith { a1, a2 ->
                                val idx1 = savedOrder.indexOf(a1.accountId)
                                val idx2 = savedOrder.indexOf(a2.accountId)
                                when {
                                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                    idx1 != -1 -> -1
                                    idx2 != -1 -> 1
                                    else -> 0
                                }
                            }
                        } else {
                            mappedAccounts
                        }
                        
                        val orderedHoldings = if (savedOrder.isNotEmpty()) {
                            realEstateResponse.data.sortedWith { h1, h2 ->
                                val idx1 = savedOrder.indexOf(h1.realEstateHoldingId)
                                val idx2 = savedOrder.indexOf(h2.realEstateHoldingId)
                                when {
                                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                    idx1 != -1 -> -1
                                    idx2 != -1 -> 1
                                    else -> 0
                                }
                            }
                        } else {
                            realEstateResponse.data
                        }
                        
                        ManageAccountsScreenState.Loaded(
                            items = items,
                            accounts = orderedAccounts,
                            realEstateHoldings = orderedHoldings,
                            accountOrder = savedOrder
                        )
                    }
                    else -> ManageAccountsScreenState.Loading
                }
            }.collect { newState ->
                _state.update { newState }
            }
        }
    }

    fun confirmUnlink(itemId: String) {
        _state.update { s ->
            if (s is ManageAccountsScreenState.Loaded) s.copy(unlinkingItemId = itemId) else s
        }
    }

    fun dismissUnlink() {
        _state.update { s ->
            if (s is ManageAccountsScreenState.Loaded) s.copy(unlinkingItemId = null) else s
        }
    }

    fun executeUnlink(itemId: String) {
        viewModelScope.launch {
            try {
                accountUseCase.UnlinkPlaidItem(itemId)
                plaidItemsEventBus.itemsChanged()
                load()
            } catch (e: Exception) {
                _state.update { ManageAccountsScreenState.Error(e.message ?: "Failed to unlink account") }
            }
        }
    }

    fun updateAccountOrder(order: List<String>) {
        val userId = currentUserId ?: return
        settingsManager.setAccountOrder(userId, order)
        
        // Update the current Loaded state directly so the UI updates instantly
        val currentState = _state.value
        if (currentState is ManageAccountsScreenState.Loaded) {
            val sortedAccounts = currentState.accounts.sortedWith { a1, a2 ->
                val idx1 = order.indexOf(a1.accountId)
                val idx2 = order.indexOf(a2.accountId)
                when {
                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                    idx1 != -1 -> -1
                    idx2 != -1 -> 1
                    else -> 0
                }
            }
            val sortedHoldings = currentState.realEstateHoldings.sortedWith { h1, h2 ->
                val idx1 = order.indexOf(h1.realEstateHoldingId)
                val idx2 = order.indexOf(h2.realEstateHoldingId)
                when {
                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                    idx1 != -1 -> -1
                    idx2 != -1 -> 1
                    else -> 0
                }
            }
            _state.update { currentState.copy(accounts = sortedAccounts, realEstateHoldings = sortedHoldings, accountOrder = order) }
        }

        viewModelScope.launch {
            plaidItemsEventBus.orderChanged()
        }
    }
}
